import posthog from "posthog-js"
import { type UserInfo } from "store/user/userSlice"
import { logger } from "utils/logger"
import { readMarketingAttribution } from "services/marketingAttribution"
import { typedEnv } from "@/config/typedEnv"

export type PlatformAnalyticsEventName =
  | "registration_button_tapped"
  | "paywall_viewed"
  | "pricing_plan_selected"
  | "stripe_checkout_accepted"
  | "product_list_viewed"
  | "product_setup_completed"
  | "get_link_taped"
  | "qr_code_taped"

type PlatformAnalyticsPropertyValue = string | number | boolean | null | undefined

type PlatformAnalyticsProperties = Record<string, PlatformAnalyticsPropertyValue>

const POSTHOG_DEFAULTS = "2026-01-30"
const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com"

let initialized = false
let currentDistinctId = ""

const normalizeProperties = (properties?: PlatformAnalyticsProperties) =>
  Object.fromEntries(Object.entries(properties ?? {}).filter(([, value]) => value !== undefined))

export const resolvePlatformDistinctId = (userInfo: Pick<UserInfo, "merchantId" | "username">) => {
  const merchantId = userInfo.merchantId.trim()
  if (merchantId) return `merchant:${merchantId}`

  const username = userInfo.username.trim()
  return username ? `platform:${username}` : ""
}

export const initPlatformAnalytics = () => {
  if (initialized) return true
  if (typedEnv.VITE_POSTHOG_ENABLED === "false") return false

  const apiKey = typedEnv.VITE_POSTHOG_KEY?.trim()
  if (!apiKey) return false

  posthog.init(apiKey, {
    api_host: typedEnv.VITE_POSTHOG_HOST ?? DEFAULT_POSTHOG_HOST,
    autocapture: false,
    capture_pageleave: false,
    capture_pageview: false,
    defaults: POSTHOG_DEFAULTS,
    disable_session_recording: typedEnv.VITE_POSTHOG_SESSION_RECORDING_ENABLED !== "true",
    mask_all_element_attributes: true,
    mask_all_text: true,
    session_recording: {
      maskAllInputs: true,
      recordBody: false,
      recordHeaders: false,
    },
  })
  initialized = true
  registerMarketingAttribution()
  return true
}

// Attaches persisted ad-click attribution (gclid/gbraid/wbraid + UTM) to every
// PostHog event, so the PostHog -> Google Ads conversion destination can match
// conversions back to the originating click. Person properties are attached on
// identify only — setting them pre-identify would force a person profile (and
// identified-events billing) for every anonymous ad visitor.
const registerMarketingAttribution = () => {
  const attribution = readMarketingAttribution()
  if (!attribution) return

  try {
    posthog.register(attribution)
  } catch (error) {
    logger.warn("analytics", "posthog_attribution_register_failed", {
      errorType: error instanceof Error ? error.name : "unknown_error",
    })
  }
}

const attachAttributionToPerson = () => {
  const attribution = readMarketingAttribution()
  if (!attribution) return

  try {
    posthog.setPersonProperties(
      attribution,
      Object.fromEntries(
        Object.entries(attribution).map(([key, value]) => [`initial_${key}`, value])
      )
    )
  } catch (error) {
    logger.warn("analytics", "posthog_attribution_person_failed", {
      errorType: error instanceof Error ? error.name : "unknown_error",
    })
  }
}

export const identifyPlatformUser = (userInfo: UserInfo) => {
  if (!initPlatformAnalytics()) return

  const distinctId = resolvePlatformDistinctId(userInfo)
  if (!distinctId || distinctId === currentDistinctId) return

  const isAdmin = userInfo.groups.some((group) => group.trim().toLowerCase() === "admin")
  posthog.identify(distinctId, {
    integration_type: "COMMON",
    is_admin: isAdmin,
    merchant_id: userInfo.merchantId || undefined,
    user_status: userInfo.status,
  })

  if (userInfo.merchantId) posthog.group("merchant", userInfo.merchantId)
  currentDistinctId = distinctId
  attachAttributionToPerson()
}

export const resetPlatformAnalytics = () => {
  if (!initialized) return

  posthog.reset()
  currentDistinctId = ""
  registerMarketingAttribution()
}

export const capturePlatformEvent = (
  event: PlatformAnalyticsEventName,
  properties?: PlatformAnalyticsProperties
) => {
  if (!initPlatformAnalytics()) return

  try {
    const attribution = readMarketingAttribution() ?? {}

    posthog.capture(event, {
      ...attribution,
      ...normalizeProperties(properties),
      service_name: "platform_web",
      service_stage: typedEnv.MODE || "unknown",
    })
  } catch (error) {
    logger.warn("analytics", "posthog_capture_failed", {
      event,
      errorType: error instanceof Error ? error.name : "unknown_error",
    })
  }
}
